import React, { FC, useCallback, useEffect, useRef, useState } from 'react'

import Masonry, { ResponsiveMasonry } from 'react-responsive-masonry'

import { searchOperation } from 'api/clientStatisticApi'
import { ProfileItem } from 'api/search/search.types'
import { VoidHandler } from 'common/types'
import { SearchItemPromo } from 'common-constants/promoCases'
import { ItemSwitcherLegacy } from 'components/page/Search/ItemSwitcherLegacy'
import {
  ListItem,
  Grid,
  GridExtend,
} from 'components/page/Search/Search.styled'
import { ScrollLoader } from 'components/system/ScrollLoader'
import { PerformanceEnd } from 'components/system/statistic/PerfomanceEnd'
import { useAuthorized } from 'hooks/useAuthorized'

import { SearchPromoTypes } from './Search.types'

const breakpointColumns = {
  460: 2,
  690: 3,
  1280: 4,
  1300: 5,
}

export const SearchListGrid: FC<{
  items: (ProfileItem | SearchItemPromo)[]
  loading: boolean
  loaded: boolean
  onLoadNewChunk: VoidHandler
  isEnd: boolean
}> = ({ items, loading, loaded, onLoadNewChunk, isEnd }) => {
  const searchContainerRef = useRef<HTMLDivElement | null>(null)
  const authorized = useAuthorized()
  const [localLoading, setLocalLoading] = useState(false)

  const getNearElementByIndex = (promoIndex: number) => {
    const searchGridContainer =
      document.getElementsByClassName('grid-container')[0]

    if (!searchGridContainer) {
      return console.warn('Grid wrapper not found')
    }

    /**
     * Получаем индекс итема следующего за промо-элементом,
     * а также количество колонок на данный момент времени.
     */
    const nearElementIndex = promoIndex + 1
    const columns = searchGridContainer.children.length

    /**
     * Высчитываем колонку и строку, для получения итема.
     */
    const gridColumn = nearElementIndex % columns // от 0 до columns-1`
    const gridRow = Math.floor(nearElementIndex / columns) // от 0 до columns-1

    /**
     * В случае если следующий после промо-элемента итем находится уже на другой строке,
     * то высоту промо-элементу задавать не нужно, потому не возращаем итем.
     */
    if (gridColumn === 0) {
      return null
    }

    /**
     * Получаем итем по колонке и строке в сетке.
     */
    return searchGridContainer.children[gridColumn].children[gridRow]
  }

  const handleLoad = useCallback(() => {
    if (!loading && !isEnd && !localLoading) {
      setLocalLoading(true)
      onLoadNewChunk()
    }
  }, [isEnd, loading, onLoadNewChunk, localLoading])

  useEffect(() => {
    if (!loading) {
      setLocalLoading(false)
    }
  }, [loading])

  useEffect(() => {
    window.scroll(0, 0)
  }, [])

  /**
   * Разделили пока на два.
   * Для авторизованного будет через mansory делать
   * Для неавторизованного через grid
   * Возможно когда будет нативный можно убрать ветку.
   */
  if (authorized) {
    return (
      <ScrollLoader onShouldLoad={handleLoad} loading={loading}>
        <GridExtend ref={searchContainerRef}>
          <ResponsiveMasonry columnsCountBreakPoints={breakpointColumns}>
            <Masonry className="grid-container">
              {items.map((item, index) => {
                const locator = findLocator(item)

                return (
                  <ListItem key={index} data-name={locator}>
                    <ItemSwitcherLegacy
                      getNearElementByIndex={getNearElementByIndex}
                      item={item}
                      itemIndex={index}
                    />
                  </ListItem>
                )
              })}
            </Masonry>
          </ResponsiveMasonry>
          {loaded && <PerformanceEnd operation={searchOperation} />}
        </GridExtend>
      </ScrollLoader>
    )
  }

  return (
    <ScrollLoader onShouldLoad={onLoadNewChunk} loading={loading}>
      <Grid className="grid-container" ref={searchContainerRef}>
        {items.map((item, index) => {
          const locator = findLocator(item)
          return (
            <ListItem key={index} data-name={locator}>
              <ItemSwitcherLegacy
                getNearElementByIndex={getNearElementByIndex}
                item={item}
                itemIndex={index}
              />
            </ListItem>
          )
        })}
        {loaded && <PerformanceEnd operation={searchOperation} />}
      </Grid>
    </ScrollLoader>
  )
}

const findLocator = (item: ProfileItem | SearchItemPromo): string => {
  switch (item.type) {
    case SearchPromoTypes.Service:
      return `search-list-${item.case}`
    default:
      return 'search-list-item'
  }
}
